import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Clock, CheckCircle, XCircle, Activity, PhoneCall, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { roomLineTotal, offerNetFactor, formatMoneyTR } from '../../utils/pricing';
import { useSeason } from '../../context/SeasonContext';
import { offerInSeason } from '../../utils/season';
import { API_URL, readJsonOrThrow } from '../../utils/api';
import { dateKey, todayKey, formatDateTR, formatStay, offerRoomsText, offerMoneyText } from '../../utils/offerShare';
import { followUpList, followUpAgeText, checkInSoonText, staleOffers } from '../../utils/followUp';
import { emailKey, phoneKey } from '../../utils/text';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const EMPTY = [];
const FOLLOW_UP_LIMIT = 8;
const ONLY_MINE_KEY = 'ramada_followup_only_mine';
// Girişe bu kadar gün ya da daha az kalan bekleyen teklif turuncu rozetle gösterilir (daha uzaksa mavi)
const CHECK_IN_URGENT_DAYS = 3;

const readOnlyMine = () => {
  try {
    return localStorage.getItem(ONLY_MINE_KEY) === '1';
  } catch {
    return false; // gizli sekmede localStorage erişimi hata verebilir
  }
};
const writeOnlyMine = (value) => {
  try { localStorage.setItem(ONLY_MINE_KEY, value ? '1' : '0'); } catch { /* yoksay */ }
};

// Teklif listesinde o müşteriyi bulduran arama: telefon varsa telefon, yoksa ad
const offerSearchText = (offer) => {
  const phone = String(offer?.phone || '').trim();
  return phoneKey(phone) ? phone : String(offer?.customerName || '').trim();
};
const offersUrl = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
  const s = qs.toString();
  return s ? `/offers?${s}` : '/offers';
};

const AGE_BADGE = {
  late: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  fresh: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};
const CHECK_IN_BADGE = {
  urgent: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  soon: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

export default function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { data, fetchData, refreshData, loading, errors } = useData();
  const { selectedSeason } = useSeason();
  const [stats, setStats] = useState({
    amount: { total: 0, pending: 0, approved: 0, rejected: 0 },
    count: { total: 0, pending: 0, approved: 0, rejected: 0 }
  });
  const [chartsData, setChartsData] = useState({
    roomRevenue: [],
    statusDist: [],
    sourceDist: [],
    timeline: []
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [onlyMine, setOnlyMine] = useState(readOnlyMine);

  // Okuma başarısızsa DataContext önceki listeyi (yoksa []) korur ve errors.offers'a mesajı yazar.
  // offersUnavailable: elde hiç teklif yokken hata → kartlar/listeler "0 TL", "takip edilecek yok" gibi
  // yanıltıcı değerler yerine "—" ve "yüklenemedi" gösterir. Önceki liste varsa o gösterilir, uyarı bunu belirtir.
  const allOffers = Array.isArray(data.offers) ? data.offers : EMPTY;
  const offersError = errors?.offers || '';
  const offersUnavailable = !!offersError && allOffers.length === 0;
  const offersRetrying = !!loading.offers;
  const retryOffers = () => { if (!offersRetrying) refreshData('offers'); };

  // Seçili sezon: kartlardaki toplamlarla aynı küme olmalı (yoksa "Onaylandı: 0" yazarken liste dolu görünür)
  const recentApprovedOffers = allOffers
    .filter(o => o.status === 'Onaylandı' && offerInSeason(o, selectedSeason))
    .slice(0, 5);

  const canViewFinancials = currentUser?.permissions?.view_financials;
  const myEmail = emailKey(currentUser?.email);
  // "Yalnızca benim tekliflerim" açıkken teklif listesine giden takip linkleri de aynı süzgeci taşır (?creator=…)
  const creatorParam = onlyMine && myEmail ? String(currentUser?.email || '').trim() : '';

  // "Bugün kimi aramalıyım?" — seçili sezondaki bekleyen oda teklifleri (isteğe bağlı: yalnızca benim kayıtlarım)
  const followUp = useMemo(() => {
    const now = new Date();
    const pool = allOffers.filter(o =>
      offerInSeason(o, selectedSeason) &&
      (!onlyMine || (!!myEmail && emailKey(o?.createdBy?.email) === myEmail))
    );
    return { list: followUpList(pool, now), staleCount: staleOffers(pool, now).length };
  }, [allOffers, selectedSeason, onlyMine, myEmail]);

  const toggleOnlyMine = (e) => {
    const next = e.target.checked;
    setOnlyMine(next);
    writeOnlyMine(next);
  };

  useEffect(() => {
    fetchData('offers');
    fetchData('roomTypes');
  }, [fetchData]);

  useEffect(() => {
    const fetchRecentLogs = async () => {
      try {
        const response = await fetch(`${API_URL}/activityLogs`, {
            headers: { 'Authorization': `Bearer ${currentUser?.token}` }
        });
        const logs = await readJsonOrThrow(response, 'İşlem kayıtları alınamadı.');
        setRecentLogs(Array.isArray(logs) ? logs.slice(0, 5) : []);
      } catch (error) {
        console.error("Loglar çekilemedi:", error);
      }
    };
    if (currentUser?.token) fetchRecentLogs();
  }, [currentUser]);

  useEffect(() => {
    if (Array.isArray(data.offers)) {
        // Üstteki sezon seçicisi: toplamlar ve grafikler yalnızca seçili sezonu kapsar
        const offers = data.offers.filter(o => offerInSeason(o, selectedSeason));
        const roomTypes = Array.isArray(data.roomTypes) ? data.roomTypes : [];

        // Güncel oda isimlerini ID'ye göre haritala
        const roomNames = {};
        roomTypes.forEach(rt => roomNames[rt.id] = rt.name);

        const calculateTotal = (items) => items.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0);

        const pendingOffers = offers.filter(o => o.status === 'Onay Bekliyor');
        const approvedOffers = offers.filter(o => o.status === 'Onaylandı');
        const rejectedOffers = offers.filter(o => o.status === 'Onaylanmadı');

        setStats({
          amount: {
            total: calculateTotal(offers),
            pending: calculateTotal(pendingOffers),
            approved: calculateTotal(approvedOffers),
            rejected: calculateTotal(rejectedOffers)
          },
          count: {
            total: offers.length,
            pending: pendingOffers.length,
            approved: approvedOffers.length,
            rejected: rejectedOffers.length
          }
        });

        // Grafikler için Veri Hazırlığı

        // 1. Oda Tipine Göre Ciro (Sadece Onaylananlar)
        const roomRevenueMap = {};
        roomTypes.forEach(rt => {
            roomRevenueMap[rt.name] = 0;
        });

        offers.forEach(o => {
            if (o.status === 'Onaylandı') {
                if (o.rooms && Array.isArray(o.rooms)) {
                    const netFactor = offerNetFactor(o); // teklif indirimi odalara oranla dağıtılır
                    o.rooms.forEach(room => {
                        const rName = roomNames[room.id] || room.name || 'Bilinmiyor';
                        const rTotal = roomLineTotal(room) * netFactor;
                        roomRevenueMap[rName] = (roomRevenueMap[rName] || 0) + rTotal;
                    });
                } else if (o.room) {
                    const rName = (o.room.id && roomNames[o.room.id]) || o.room.name || 'Bilinmiyor';
                    const rTotal = Number(o.totalPrice) || 0;
                    roomRevenueMap[rName] = (roomRevenueMap[rName] || 0) + rTotal;
                }
            }
        });
        const roomRevenueData = Object.keys(roomRevenueMap).map(key => ({ name: key, Ciro: roomRevenueMap[key] }));

        // 2. Teklif Durum Dağılımı
        const statusCounts = {};
        offers.forEach(o => {
            const status = o.status || 'Bilinmiyor';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        const statusData = Object.keys(statusCounts).map(key => ({ name: key, value: statusCounts[key] }));

        // 3. Teklif Kaynak Dağılımı
        const sourceCounts = {};
        offers.forEach(o => {
            const sourceName = o.source?.name || 'Bilinmiyor';
            sourceCounts[sourceName] = (sourceCounts[sourceName] || 0) + 1;
        });
        const sourceData = Object.keys(sourceCounts).map(key => ({ name: key, value: sourceCounts[key] }));

        // 4. Zaman İçinde Ciro (Günlük - Bu Ay). Gün = kayıt tarihinin İstanbul günü (dateKey) — Raporlar ile aynı kural.
        //    Tarihi okunamayan kayıt grafiğe girmez ("bugün" yedeği yok: eskiden her onaylı teklif bugüne yazılıyordu).
        const today = todayKey();
        const monthPrefix = today.slice(0, 7); // 'YYYY-MM'
        const daysInMonth = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0).getDate();

        const timelineMap = {};
        offers.forEach(o => {
            if (o.status !== 'Onaylandı') return;
            const key = dateKey(o.createdAt);
            if (!key || key.slice(0, 7) !== monthPrefix) return;
            const day = Number(key.slice(8, 10));
            timelineMap[day] = (timelineMap[day] || 0) + (Number(o.totalPrice) || 0);
        });

        const timelineData = [];
        for (let i = 1; i <= daysInMonth; i++) {
            timelineData.push({
                name: i.toString(),
                Ciro: timelineMap[i] || 0
            });
        }

        setChartsData({
            roomRevenue: roomRevenueData,
            statusDist: statusData,
            sourceDist: sourceData,
            timeline: timelineData
        });
    }
  }, [data.offers, data.roomTypes, selectedSeason]);

  // null = henüz hiç okunmadı (ilk çizimde istek daha başlamamış olabilir): "0 TL" yerine yükleniyor.
  // Okuma bitince DataContext her durumda dizi yazar (hata → önceki liste ya da []).
  if (!Array.isArray(data.offers)) return <LoadingSpinner />;

  // value === null → veri yok (teklifler yüklenemedi): "0" yerine "—"
  const StatCard = ({ title, value, icon: Icon, colorClass, bgClass, suffix, onClick }) => {
    const isMoney = suffix.includes('TL');
    const text = value === null
      ? '—'
      : !isMoney ? value.toLocaleString('tr-TR') + suffix : canViewFinancials ? formatMoneyTR(value) + suffix : '*** TL';
    return (
    <div
        onClick={onClick}
        className={`bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-between ${onClick ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
    >
      <div className="min-w-0">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 font-medium">{title}</p>
        <h3 className={`text-2xl font-bold ${value === null ? 'text-gray-400 dark:text-gray-500' : colorClass}`} title={value === null ? 'Teklifler yüklenemedi' : undefined}>{text}</h3>
      </div>
      <div className={`p-3 rounded-full ${bgClass} ${colorClass} bg-opacity-20`}>
        <Icon size={24} />
      </div>
    </div>
    );
  };

  const followUpCount = followUp.list.length;
  const cardValue = (v) => (offersUnavailable ? null : v);

  // Teklifler yüklenemediğinde grafik/liste yerine gösterilen nötr kutu
  const Unavailable = ({ className = 'h-64' }) => (
    <div className={`${className} flex items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-900/40 text-sm text-gray-400 dark:text-gray-500 text-center px-4`}>
      — Teklifler yüklenemedi
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Genel Bakış</h1>

      {offersError && (
        <div role="alert" className="mb-6 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium">
                {offersUnavailable ? 'Teklifler yüklenemedi.' : 'Teklifler güncellenemedi — aşağıdaki bilgiler son başarılı yüklemeye ait.'}
              </p>
              <p className="break-words opacity-90">{offersError}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={retryOffers}
            disabled={offersRetrying}
            className="self-start sm:self-auto shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 disabled:cursor-wait dark:bg-red-700 dark:hover:bg-red-600"
          >
            <RefreshCw size={14} className={offersRetrying ? 'animate-spin' : ''} />
            {offersRetrying ? 'Yükleniyor…' : 'Tekrar dene'}
          </button>
        </div>
      )}

      {/* Bugün takip edilecekler: 3+ / 7+ gündür cevapsız ya da girişi yaklaşan bekleyen teklifler */}
      <section className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <PhoneCall size={18} className="shrink-0 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">Bugün takip edilecekler</h2>
            <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              {offersUnavailable ? '—' : followUpCount}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={toggleOnlyMine}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
              />
              Yalnızca benim tekliflerim
            </label>
            {followUpCount > 0 && (
              <button
                type="button"
                onClick={() => navigate(offersUrl({ filter: 'takip', creator: creatorParam }))}
                className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Tümünü gör <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>

        {followUp.staleCount > 0 && (
          <button
            type="button"
            onClick={() => navigate(offersUrl({ filter: 'gecmis', creator: creatorParam }))}
            className="w-full mb-4 flex items-start gap-2 text-left text-sm px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30 transition-colors"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>Giriş tarihi geçmiş {followUp.staleCount} teklif hâlâ 'Onay Bekliyor'</span>
          </button>
        )}

        {offersUnavailable ? (
          <div className="flex items-center gap-3 px-3 py-4 rounded-lg bg-gray-50 text-sm text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
            <AlertTriangle size={18} className="shrink-0 text-red-500 dark:text-red-400" />
            <span>Teklifler yüklenemediği için takip listesi gösterilemiyor. Yukarıdan "Tekrar dene"yi kullanın.</span>
          </div>
        ) : followUpCount === 0 ? (
          <div className="flex items-center gap-3 px-3 py-4 rounded-lg bg-green-50 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-300">
            <CheckCircle size={18} className="shrink-0" />
            <span>Bugün aranması gereken teklif yok — bekleyen teklifler güncel.</span>
          </div>
        ) : (
          <>
            <ul className="-mx-2 divide-y divide-gray-100 dark:divide-gray-700">
              {followUp.list.slice(0, FOLLOW_UP_LIMIT).map(({ offer, f }) => {
                const ageText = followUpAgeText(f);
                const soonText = checkInSoonText(f);
                const details = [offerRoomsText(offer), formatStay(offer)].filter(Boolean).join(' · ');
                return (
                  <li key={offer.id}>
                    <button
                      type="button"
                      onClick={() => navigate(offersUrl({ q: offerSearchText(offer) }))}
                      className="w-full text-left px-2 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {offer.customerName || 'İsimsiz müşteri'}
                          {offer.phone && (
                            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{offer.phone}</span>
                          )}
                        </p>
                        {details && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{details}</p>}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:shrink-0">
                        {canViewFinancials && (
                          <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{offerMoneyText(offer)}</span>
                        )}
                        {soonText && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.daysToCheckIn <= CHECK_IN_URGENT_DAYS ? CHECK_IN_BADGE.urgent : CHECK_IN_BADGE.soon}`}>
                            {soonText}
                          </span>
                        )}
                        {ageText && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${AGE_BADGE[f.level] || AGE_BADGE.fresh}`}>
                            {ageText}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            {followUpCount > FOLLOW_UP_LIMIT && (
              <button
                type="button"
                onClick={() => navigate(offersUrl({ filter: 'takip', creator: creatorParam }))}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                +{followUpCount - FOLLOW_UP_LIMIT} teklif daha — tümünü gör
              </button>
            )}
          </>
        )}
      </section>

      <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">Finansal Durum</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Toplam Tutar" value={cardValue(stats.amount.total)} icon={FileText} colorClass="text-blue-600 dark:text-blue-400" bgClass="bg-blue-50 dark:bg-blue-900/20" suffix=" TL" />
        <StatCard title="Bekleyen Tutar" value={cardValue(stats.amount.pending)} icon={Clock} colorClass="text-yellow-600 dark:text-yellow-400" bgClass="bg-yellow-50 dark:bg-yellow-900/20" suffix=" TL" />
        <StatCard title="Onaylanan Tutar" value={cardValue(stats.amount.approved)} icon={CheckCircle} colorClass="text-green-600 dark:text-green-400" bgClass="bg-green-50 dark:bg-green-900/20" suffix=" TL" />
        <StatCard title="Reddedilen Tutar" value={cardValue(stats.amount.rejected)} icon={XCircle} colorClass="text-red-600 dark:text-red-400" bgClass="bg-red-50 dark:bg-red-900/20" suffix=" TL" />
      </div>

      <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">Teklif Adetleri</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
            title="Toplam Teklif"
            value={cardValue(stats.count.total)}
            icon={FileText}
            colorClass="text-blue-600 dark:text-blue-400"
            bgClass="bg-blue-50 dark:bg-blue-900/20"
            suffix=" Adet"
            onClick={() => navigate('/offers')}
        />
        <StatCard
            title="Bekleyen Teklif"
            value={cardValue(stats.count.pending)}
            icon={Clock}
            colorClass="text-yellow-600 dark:text-yellow-400"
            bgClass="bg-yellow-50 dark:bg-yellow-900/20"
            suffix=" Adet"
            onClick={() => navigate('/offers?status=' + encodeURIComponent('Onay Bekliyor'))}
        />
        <StatCard
            title="Onaylanan Teklif"
            value={cardValue(stats.count.approved)}
            icon={CheckCircle}
            colorClass="text-green-600 dark:text-green-400"
            bgClass="bg-green-50 dark:bg-green-900/20"
            suffix=" Adet"
            onClick={() => navigate('/offers?status=' + encodeURIComponent('Onaylandı'))}
        />
        <StatCard
            title="Reddedilen Teklif"
            value={cardValue(stats.count.rejected)}
            icon={XCircle}
            colorClass="text-red-600 dark:text-red-400"
            bgClass="bg-red-50 dark:bg-red-900/20"
            suffix=" Adet"
            onClick={() => navigate('/offers?status=' + encodeURIComponent('Onaylanmadı'))}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-start">
        {/* Oda Tipine Göre Ciro */}
        {canViewFinancials && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-4">Oda Tipine Göre Ciro</h3>
            {offersUnavailable ? <Unavailable className="h-64" /> : (
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartsData.roomRevenue} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis type="number" stroke="#9ca3af" />
                        <YAxis dataKey="name" type="category" width={80} stroke="#9ca3af" style={{fontSize: '10px'}} />
                        <Tooltip
                            formatter={(value) => `${formatMoneyTR(value)} TL`}
                            contentStyle={{ backgroundColor: darkMode ? '#1F2937' : '#fff', borderRadius: '12px', border: darkMode ? '1px solid #374151' : 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', color: darkMode ? '#fff' : '#000' }}
                            itemStyle={{ color: darkMode ? '#fff' : '#000' }}
                            cursor={{ fill: '#f3f4f6' }}
                        />
                        <Legend />
                        <Bar dataKey="Ciro" fill="#0088FE" radius={[0, 4, 4, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            )}
        </div>
        )}

        {/* Teklif Durum Dağılımı */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-4">Teklif Durum Dağılımı</h3>
            {offersUnavailable ? <Unavailable className="h-64" /> : (
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartsData.statusDist}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            fill="#8884d8"
                            paddingAngle={5}
                            dataKey="value"
                            label={({name, percent}) => `${(percent * 100).toFixed(0)}%`}
                        >
                            {chartsData.statusDist.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            contentStyle={{ backgroundColor: darkMode ? '#1F2937' : '#fff', borderRadius: '12px', border: darkMode ? '1px solid #374151' : 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', color: darkMode ? '#fff' : '#000' }}
                            itemStyle={{ color: darkMode ? '#fff' : '#000' }}
                        />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            )}
        </div>

        {/* Teklif Kaynak Dağılımı */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-4">Teklif Kaynak Dağılımı</h3>
            {offersUnavailable ? <Unavailable className="h-64" /> : (
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartsData.sourceDist}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            fill="#82ca9d"
                            paddingAngle={5}
                            dataKey="value"
                            label={({name, percent}) => `${(percent * 100).toFixed(0)}%`}
                        >
                            {chartsData.sourceDist.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            contentStyle={{ backgroundColor: darkMode ? '#1F2937' : '#fff', borderRadius: '12px', border: darkMode ? '1px solid #374151' : 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', color: darkMode ? '#fff' : '#000' }}
                            itemStyle={{ color: darkMode ? '#fff' : '#000' }}
                        />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            )}
        </div>

        {/* Son İşlemler (Yeni Eklenen Bölüm) */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200">Son İşlemler</h3>
                <Activity size={18} className="text-gray-400" />
            </div>
            <div className="space-y-4">
                {recentLogs.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">İşlem kaydı bulunamadı.</p>
                ) : (
                    recentLogs.map(log => (
                        <div key={log.id} className="flex items-start gap-3 pb-3 border-b border-gray-50 dark:border-gray-700 last:border-0 last:pb-0">
                            <div className="mt-1 min-w-[8px] h-2 rounded-full bg-blue-500"></div>
                            <div>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{log.action}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1" title={log.description}>{log.description}</p>
                                <p className="text-[10px] text-gray-400 mt-1">{log.timestamp ? new Date(log.timestamp).toLocaleString('tr-TR') : ''} - {log.performedBy?.email?.split('@')[0]}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>

        {/* En Son Onaylananlar */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200">En Son Onaylananlar</h3>
                <CheckCircle size={18} className="text-green-500" />
            </div>
            <div className="space-y-4">
                {offersUnavailable ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">— Teklifler yüklenemedi</p>
                ) : recentApprovedOffers.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Henüz onaylanan teklif yok.</p>
                ) : (
                    recentApprovedOffers.map(offer => (
                        <div key={offer.id} className="flex items-start gap-3 pb-3 border-b border-gray-50 dark:border-gray-700 last:border-0 last:pb-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded p-2 -mx-2 transition-colors" onClick={() => navigate(offersUrl({ status: 'Onaylandı', q: offerSearchText(offer) }))}>
                            <div className="mt-1 min-w-[8px] h-2 rounded-full bg-green-500"></div>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start gap-2">
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{offer.customerName}</p>
                                    <span className="shrink-0 text-xs font-bold text-blue-600 dark:text-blue-400">
                                        {canViewFinancials ? `${formatMoneyTR(Number(offer.totalPrice))} ${offer.currency || 'TL'}` : '*** TL'}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{offerRoomsText(offer)}</p>
                                <p className="text-[10px] text-gray-400 mt-1">{formatDateTR(offer.createdAt)}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
      </div>

      {/* Zaman İçinde Ciro (Günlük) */}
      {canViewFinancials && (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8">
        <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-4">Zaman İçinde Ciro (Günlük)</h3>
        {offersUnavailable ? <Unavailable className="h-80" /> : (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartsData.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                    <XAxis dataKey="name" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip
                        formatter={(value) => `${formatMoneyTR(value)} TL`}
                        contentStyle={{ backgroundColor: darkMode ? '#1F2937' : '#fff', borderRadius: '12px', border: darkMode ? '1px solid #374151' : 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', color: darkMode ? '#fff' : '#000' }}
                        itemStyle={{ color: darkMode ? '#fff' : '#000' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="Ciro" stroke="#0088FE" strokeWidth={3} activeDot={{ r: 8 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
        )}
      </div>
      )}
    </div>
  );
}
